import { valueLabel, valueError, valueNotice } from "../shared/value-copy";
import { useEffect, useMemo, useRef, useState } from "react";
import { localDateTimeErrorMessage, localTimeZoneHint, resolveLocalDateTime, toLocalDateTimeInput } from "../shared/local-date-time";
import {
  Alert,
  Button,
  Card,
  Checkbox,
  DataTable,
  Dialog,
  Field,
  Grid,
  Inline,
  KpiCard,
  PageHeader,
  Panel,
  SelectField,
  Stack,
  StatusBadge,
  Switch,
  Tab,
  Tabs,
  TextAreaField,
  formatMoneyMinor,
  useToast
} from "@leylion/design-system";
import {
  addWaitlistEntry,
  assignDeliveryOrder,
  getDeliveryBoard,
  getDeliveryDrivers,
  getFloorPlan,
  getHandovers,
  getKitchenBoard,
  getKitchenRoutingRules,
  getKitchenStations,
  getProducts,
  getRecipes,
  getReservations,
  getRestaurantOverview,
  getWaitlist,
  saveDeliveryDriver,
  saveKitchenRoutingRule,
  saveKitchenStation,
  saveRecipe,
  saveReservation,
  setKitchenTicketPriority,
  transitionDeliveryOrder,
  transitionHandover,
  transitionKitchenItem,
  transitionKitchenTicket,
  transitionReservation,
  transitionWaitlistEntry
} from "../api/client";
import type { Product } from "../api/catalog-types";
import type { Customer, DiningFloor } from "../api/cashier-types";
import { CashierCustomerSearchPicker } from "../cashier/CashierCustomerSearchPicker";
import type {
  DeliveryDriver,
  DeliveryOrder,
  DriverStatus,
  Handover,
  HandoverRecipientType,
  KitchenBoard,
  KitchenPriority,
  KitchenRoutingRule,
  KitchenStation,
  Recipe,
  Reservation,
  RestaurantOverview,
  WaitlistEntry
} from "../api/restaurant-types";
import "./restaurant.css";
import { DiningConfiguration } from "./DiningConfiguration";

const noReservationCustomers: Customer[] = [];
type Language = "ar" | "en";
type RestaurantTab =
  | "overview"
  | "kds"
  | "handover"
  | "reservations"
  | "delivery"
  | "recipes"
  | "configuration"
  | "dining";

interface VariantOption {
  id: string;
  productId: string;
  categoryId: string;
  productNameAr: string;
  productNameEn: string;
  variantNameAr: string;
  variantNameEn: string;
  sku: string;
  tracked: boolean;
  sellable: boolean;
}

export function RestaurantOperationsCenter({
  language,
  permissions
}: {
  language: Language;
  permissions: string[];
}) {
  const ar = language === "ar";
  const [tab, setTab] = useState<RestaurantTab>("overview");
  const [refreshKey, setRefreshKey] = useState(0);
  const can = (permission: string) => permissions.includes(permission);

  const tabs: Array<[RestaurantTab, string, boolean]> = [
    ["dining", ar ? "الصالات والطاولات" : "Floors & Tables", can("tables.manage")],
    ["overview", ar ? "نظرة عامة" : "Overview", true],
    ["kds", ar ? "شاشة المطبخ" : "KDS", can("kds.read")],
    ["handover", ar ? "التسليم" : "Handover", can("kds.read")],
    ["reservations", ar ? "الحجوزات والانتظار" : "Reservations & Waitlist", can("reservations.manage")],
    ["delivery", ar ? "التوصيل" : "Delivery", can("delivery.read")],
    ["recipes", ar ? "الوصفات" : "Recipes", can("recipes.manage")],
    ["configuration", ar ? "المحطات والتوجيه" : "Stations & Routing", can("kds.read")]
  ];

  return (
    <Stack>
      <PageHeader
        eyebrow={ar ? "تشغيل المطعم" : "Restaurant Operations"}
        title={ar ? "المطبخ والحجوزات والتوصيل" : "Kitchen, Reservations & Delivery"}
        description={
          ar
            ? "دورة تشغيل متصلة من إرسال الطلب إلى المحطات، التحضير، التسليم، الحجوزات وقائمة الانتظار والتوصيل."
            : "A connected operating cycle from order fire to stations, preparation, handover, reservations, waitlist and delivery."
        }
      />

      <Tabs value={tab} onValueChange={(value) => setTab(value as RestaurantTab)} label="restaurant operations">
        {tabs.filter(([, , visible]) => visible).map(([value, label]) => (
          <Tab key={value} value={value} data-testid={`restaurant-tab-${value}`}>
            {label}
          </Tab>
        ))}
      </Tabs>

      {tab === "dining" && can("tables.manage") ? <DiningConfiguration language={language} onChanged={() => setRefreshKey(value => value + 1)} /> : tab === "overview" ? (
        <Overview
          language={language}
          refreshKey={refreshKey}
          onOpen={setTab}
        />
      ) : tab === "kds" ? (
        <KdsBoard
          language={language}
          canManage={can("kds.manage")}
          refreshKey={refreshKey}
          onChanged={() => setRefreshKey((value) => value + 1)}
        />
      ) : tab === "handover" ? (
        <HandoverBoard
          language={language}
          canManage={can("kds.manage")}
          refreshKey={refreshKey}
          onChanged={() => setRefreshKey((value) => value + 1)}
        />
      ) : tab === "reservations" ? (
        <ReservationsBoard
          canReadCustomers={can("customers.read")}
          language={language}
          refreshKey={refreshKey}
          onChanged={() => setRefreshKey((value) => value + 1)}
        />
      ) : tab === "delivery" ? (
        <DeliveryBoard
          language={language}
          canDispatch={can("delivery.dispatch")}
          refreshKey={refreshKey}
          onChanged={() => setRefreshKey((value) => value + 1)}
        />
      ) : tab === "recipes" ? (
        <RecipesBoard
          language={language}
          refreshKey={refreshKey}
          onChanged={() => setRefreshKey((value) => value + 1)}
        />
      ) : (
        <ConfigurationBoard
          language={language}
          canManage={can("kds.manage")}
          refreshKey={refreshKey}
          onChanged={() => setRefreshKey((value) => value + 1)}
        />
      )}
    </Stack>
  );
}

function Overview({
  language,
  refreshKey,
  onOpen
}: {
  language: Language;
  refreshKey: number;
  onOpen: (tab: RestaurantTab) => void;
}) {
  const ar = language === "ar";
  const [overview, setOverview] = useState<RestaurantOverview | null>(null);
  const [error, setError] = useState("");

  useEffect(() => {
    getRestaurantOverview()
      .then(setOverview)
      .catch((caught: unknown) => setError(message(caught)));
  }, [refreshKey]);

  if (error) {
    return <Alert tone="danger" title={ar ? "تعذر التحميل" : "Load failed"}>{error}</Alert>;
  }
  if (!overview) {
    return <Card>{ar ? "جارٍ تجهيز مركز تشغيل المطعم…" : "Preparing restaurant operations…"}</Card>;
  }

  return (
    <Stack>
      <Grid columns={4}>
        <KpiCard label={ar ? "طاولات مشغولة" : "Occupied tables"} value={overview.occupiedTables} />
        <KpiCard label={ar ? "طاولات متاحة" : "Available tables"} value={overview.availableTables} />
        <KpiCard label={ar ? "حجوزات قادمة" : "Upcoming reservations"} value={overview.upcomingReservations} />
        <KpiCard label={ar ? "ضيوف ينتظرون" : "Waiting guests"} value={overview.waitingGuests} trendTone={overview.waitingGuests ? "warning" : "success"} />
        <KpiCard label={ar ? "تذاكر جديدة" : "New tickets"} value={overview.newKitchenTickets} />
        <KpiCard label={ar ? "قيد التحضير" : "Preparing"} value={overview.preparingKitchenTickets} />
        <KpiCard label={ar ? "تذاكر حرجة" : "Critical tickets"} value={overview.criticalKitchenTickets} trendTone={overview.criticalKitchenTickets ? "danger" : "success"} />
        <KpiCard label={ar ? "طلبات جاهزة للتسليم" : "Ready handovers"} value={overview.readyHandovers} />
        <KpiCard label={ar ? "توصيلات دون سائق" : "Unassigned deliveries"} value={overview.unassignedDeliveries} trendTone={overview.unassignedDeliveries ? "warning" : "success"} />
        <KpiCard label={ar ? "سائقون نشطون" : "Active drivers"} value={overview.activeDrivers} />
        <KpiCard label={ar ? "متوسط المطبخ" : "Avg kitchen"} value={`${overview.averageKitchenMinutes} ${ar ? "د" : "min"}`} />
        <KpiCard label={ar ? "التوصيل في الوقت" : "On-time delivery"} value={`${overview.deliveryOnTimePercent}%`} />
      </Grid>

      {overview.warnings.length ? (
        <Alert tone="warning" title={ar ? "تحتاج انتباهًا" : "Needs attention"}>
          {overview.warnings.map(warning => valueNotice(warning, language)).join(" · ")}
        </Alert>
      ) : (
        <Alert tone="success" title={ar ? "التشغيل مستقر" : "Operations are stable"} />
      )}

      <Grid columns={4}>
        <Card interactive title={ar ? "افتح شاشة المطبخ" : "Open KDS"} onClick={() => onOpen("kds")} />
        <Card interactive title={ar ? "راجع الحجوزات" : "Review reservations"} onClick={() => onOpen("reservations")} />
        <Card interactive title={ar ? "تابع التوصيل" : "Track delivery"} onClick={() => onOpen("delivery")} />
        <Card interactive title={ar ? "إدارة الوصفات" : "Manage recipes"} onClick={() => onOpen("recipes")} />
      </Grid>
    </Stack>
  );
}

function KdsBoard({
  language,
  canManage,
  refreshKey,
  onChanged
}: {
  language: Language;
  canManage: boolean;
  refreshKey: number;
  onChanged: () => void;
}) {
  const ar = language === "ar";
  const [board, setBoard] = useState<KitchenBoard | null>(null);
  const [stationId, setStationId] = useState("");
  const [error, setError] = useState("");

  const load = async () => {
    try {
      setBoard(await getKitchenBoard(stationId || undefined));
      setError("");
    } catch (caught) {
      setError(message(caught));
    }
  };

  useEffect(() => {
    void load();
    const timer = window.setInterval(() => void load(), 15000);
    return () => window.clearInterval(timer);
  }, [stationId, refreshKey]);

  const updateTicket = async (
    ticket: KitchenBoard["tickets"][number],
    action: "accept" | "start" | "ready" | "complete" | "recall"
  ) => {
    try {
      await transitionKitchenTicket(ticket, action);
      await load();
      onChanged();
    } catch (caught) {
      setError(message(caught));
    }
  };

  return (
    <Stack>
      {error ? <Alert tone="danger" title={ar ? "تعذر تنفيذ العملية" : "Action failed"}>{error}</Alert> : null}
      <Panel
        title={ar ? "شاشة المطبخ KDS" : "Kitchen Display System"}
        description={ar ? "الأولوية والوقت والمحطة وحالة كل سطر." : "Priority, timing, station and every line status."}
        actions={
          <Inline>
            <SelectField label={ar ? "المحطة" : "Station"} value={stationId} onChange={(event) => setStationId(event.target.value)}>
              <option value="">{ar ? "كل المحطات" : "All stations"}</option>
              {board?.stations.map((station) => (
                <option key={station.id} value={station.id}>{ar ? station.nameAr : station.nameEn}</option>
              ))}
            </SelectField>
            <Button variant="secondary" onClick={() => void load()}>{ar ? "تحديث" : "Refresh"}</Button>
          </Inline>
        }
      >
        <Stack>
          {board ? (
            <Grid columns={4}>
              <KpiCard label={ar ? "جديد" : "New"} value={board.newCount} />
              <KpiCard label={ar ? "قيد التحضير" : "Preparing"} value={board.preparingCount} />
              <KpiCard label={ar ? "جاهز" : "Ready"} value={board.readyCount} />
              <KpiCard label={ar ? "حرج" : "Critical"} value={board.criticalCount} trendTone={board.criticalCount ? "danger" : "success"} />
            </Grid>
          ) : null}

          <div className="kds-grid" data-testid="kds-ticket-grid">
            {board?.tickets.length ? board.tickets.map((ticket) => (
              <article key={ticket.id} className={`kds-ticket kds-ticket--${ticket.timingTone}`} data-testid={`kds-ticket-${ticket.ticketNumber}`}>
                <header>
                  <span>
                    <strong>#{ticket.sequenceNumber} · {ticket.orderNumber}</strong>
                    <small>{ar ? ticket.stationNameAr : ticket.stationNameEn} · {valueLabel(ticket.serviceType, language)}{ticket.tableName ? ` · ${ticket.tableName}` : ""}</small>
                  </span>
                  <span className="kds-age">{ticket.ageMinutes}m</span>
                </header>

                <Inline>
                  <StatusBadge tone={ticket.status === "Ready" ? "success" : ticket.status === "Preparing" ? "warning" : "info"}>{ticket.status}</StatusBadge>
                  <StatusBadge tone={ticket.priority === "Vip" ? "danger" : ticket.priority === "Rush" ? "warning" : "neutral"}>{ticket.priority}</StatusBadge>
                  {ticket.isPreOrder ? (
                    <StatusBadge tone="warning">{ar ? "طلب مسبق" : "Pre-order"}</StatusBadge>
                  ) : null}
                </Inline>

                {ticket.isPreOrder ? (
                  <div className="kds-preorder-banner" data-testid={`kds-preorder-${ticket.id}`}>
                    <span>
                      <small>{ar ? "رقم الحجز" : "Booking"}</small>
                      <strong>{ticket.bookingNumber ?? "—"}</strong>
                    </span>
                    <span>
                      <small>{ar ? "موعد الاستلام" : "Fulfillment"}</small>
                      <strong>
                        {ticket.scheduledFulfillmentAt
                          ? new Date(ticket.scheduledFulfillmentAt).toLocaleString(ar ? "ar-AE" : "en-AE")
                          : "—"}
                      </strong>
                    </span>
                  </div>
                ) : null}

                <div className="kds-items">
                  {ticket.items.map((item) => (
                    <div key={item.id} className="kds-item">
                      <span>
                        <strong>{item.quantityMilli / 1000} × {ar ? item.productNameAr : item.productNameEn}</strong>
                        <small>{ar ? item.variantNameAr : item.variantNameEn}{(ar ? item.modifiersAr : item.modifiersEn) ? ` · ${ar ? item.modifiersAr : item.modifiersEn}` : ""}</small>
                        {item.note ? <small>{item.note}</small> : null}
                      </span>
                      <Inline>
                        <StatusBadge tone={item.status === "Ready" ? "success" : item.status === "Preparing" ? "warning" : "neutral"}>{item.status}</StatusBadge>
                        {canManage && (item.status === "New" || item.status === "Accepted") ? (
                          <Button size="sm" variant="secondary" onClick={async () => { await transitionKitchenItem(ticket, item.id, item.version, "start"); await load(); }}>
                            {ar ? "ابدأ" : "Start"}
                          </Button>
                        ) : null}
                        {canManage && item.status === "Preparing" ? (
                          <Button size="sm" onClick={async () => { await transitionKitchenItem(ticket, item.id, item.version, "ready"); await load(); }}>
                            {ar ? "جاهز" : "Ready"}
                          </Button>
                        ) : null}
                      </Inline>
                    </div>
                  ))}
                </div>

                {canManage ? (
                  <footer>
                    <Inline>
                      {ticket.status === "New" ? <Button data-testid={`kds-accept-${ticket.id}`} size="sm" variant="secondary" onClick={() => void updateTicket(ticket, "accept")}>{ar ? "قبول" : "Accept"}</Button> : null}
                      {ticket.status === "New" || ticket.status === "Accepted" || ticket.status === "Recalled" ? <Button data-testid={`kds-start-${ticket.id}`} size="sm" onClick={() => void updateTicket(ticket, "start")}>{ar ? "بدء" : "Start"}</Button> : null}
                      {ticket.status === "Preparing" ? <Button data-testid={`kds-ready-${ticket.id}`} size="sm" onClick={() => void updateTicket(ticket, "ready")}>{ar ? "جاهز" : "Ready"}</Button> : null}
                      {ticket.status === "Ready" ? <Button data-testid={`kds-complete-${ticket.id}`} size="sm" onClick={() => void updateTicket(ticket, "complete")}>{ar ? "إغلاق" : "Complete"}</Button> : null}
                      {ticket.status === "Ready" ? <Button size="sm" variant="secondary" onClick={() => void updateTicket(ticket, "recall")}>{ar ? "استدعاء" : "Recall"}</Button> : null}
                    </Inline>
                    <SelectField
                      label={ar ? "الأولوية" : "Priority"}
                      value={ticket.priority}
                      onChange={async (event) => {
                        await setKitchenTicketPriority(ticket, event.target.value as KitchenPriority);
                        await load();
                      }}
                    >
                      <option value="Normal">Normal</option>
                      <option value="Rush">Rush</option>
                      <option value="Vip">VIP</option>
                    </SelectField>
                  </footer>
                ) : null}
              </article>
            )) : <Card>{ar ? "لا توجد تذاكر فعالة." : "No active kitchen tickets."}</Card>}
          </div>
        </Stack>
      </Panel>
    </Stack>
  );
}

function HandoverBoard({
  language,
  canManage,
  refreshKey,
  onChanged
}: {
  language: Language;
  canManage: boolean;
  refreshKey: number;
  onChanged: () => void;
}) {
  const ar = language === "ar";
  const [items, setItems] = useState<Handover[]>([]);
  const [error, setError] = useState("");
  const load = async () => {
    try { setItems(await getHandovers()); setError(""); } catch (caught) { setError(message(caught)); }
  };
  useEffect(() => { void load(); }, [refreshKey]);

  const act = async (handover: Handover, action: "quality-check" | "hand-over" | "complete") => {
    try {
      const recipient: HandoverRecipientType = handover.serviceType === "DineIn" ? "Waiter" : handover.serviceType === "Delivery" ? "Driver" : "Customer";
      await transitionHandover(handover, action, recipient, handover.recipientName);
      await load(); onChanged();
    } catch (caught) { setError(message(caught)); }
  };

  return (
    <Stack>
      {error ? <Alert tone="danger" title={ar ? "تعذر التنفيذ" : "Action failed"}>{error}</Alert> : null}
      <Panel title={ar ? "فحص الجودة وتسليم الطلبات" : "Quality Check & Handover"}>
        <DataTable
          rows={items}
          rowKey={(row) => row.id}
          emptyTitle={ar ? "لا توجد طلبات جاهزة للتسليم" : "No ready handovers"}
          columns={[
            { key: "order", header: ar ? "الطلب" : "Order", cell: (row) => <span><strong>{row.orderNumber}</strong><small className="restaurant-subtext">{valueLabel(row.serviceType, language)}{row.tableName ? ` · ${row.tableName}` : ""}</small></span> },
            { key: "customer", header: ar ? "العميل" : "Customer", cell: (row) => row.customerName ?? "—" },
            { key: "age", header: ar ? "الانتظار" : "Waiting", cell: (row) => `${row.readyMinutes} ${ar ? "د" : "min"}` },
            { key: "status", header: ar ? "الحالة" : "Status", cell: (row) => <StatusBadge tone={row.status === "Completed" ? "success" : row.status === "AwaitingQualityCheck" ? "warning" : "info"}>{valueLabel(row.status, language)}</StatusBadge> },
            { key: "recipient", header: ar ? "المستلم" : "Recipient", cell: (row) => row.recipientName ?? row.recipientType },
            { key: "actions", header: "", cell: (row) => canManage ? <Inline>
              {row.status === "AwaitingQualityCheck" ? <Button size="sm" variant="secondary" onClick={() => void act(row, "quality-check")}>{ar ? "فحص" : "Check"}</Button> : null}
              {row.status === "QualityChecked" ? <Button size="sm" onClick={() => void act(row, "hand-over")}>{ar ? "تسليم" : "Hand over"}</Button> : null}
              {row.status === "HandedOver" ? <Button size="sm" onClick={() => void act(row, "complete")}>{ar ? "إكمال" : "Complete"}</Button> : null}
            </Inline> : null }
          ]}
        />
      </Panel>
    </Stack>
  );
}

function ReservationsBoard({
  language,
  canReadCustomers,
  refreshKey,
  onChanged
}: {
  language: Language;
  canReadCustomers: boolean;
  refreshKey: number;
  onChanged: () => void;
}) {
  const ar = language === "ar";
  const toast = useToast();
  const loadGeneration = useRef(0);
  const [reservations, setReservations] = useState<Reservation[]>([]);
  const [waitlist, setWaitlist] = useState<WaitlistEntry[]>([]);
  const [floors, setFloors] = useState<DiningFloor[]>([]);
  const [reservationEditor, setReservationEditor] = useState<Partial<Reservation> | null>(null);
  const [reservationCustomer, setReservationCustomer] = useState<Customer | null>(null);
  const [reservationLocalDate, setReservationLocalDate] = useState("");
  const [reservationDateError, setReservationDateError] = useState("");
  const [waitlistOpen, setWaitlistOpen] = useState(false);
  const [waitName, setWaitName] = useState("");
  const [waitPhone, setWaitPhone] = useState("");
  const [waitGuests, setWaitGuests] = useState("2");
  const [waitMinutes, setWaitMinutes] = useState("15");
  const [seatTarget, setSeatTarget] = useState<Reservation | WaitlistEntry | null>(null);
  const [seatTableId, setSeatTableId] = useState("");
  const [seatError, setSeatError] = useState("");
  const [reservationTransitionId, setReservationTransitionId] = useState<string | null>(null);
  const [error, setError] = useState("");
  const [tablesLoaded, setTablesLoaded] = useState(false);
  const [tablesError, setTablesError] = useState("");
  const [reservationError, setReservationError] = useState("");

  const load = async () => {
    const generation = ++loadGeneration.current;
    const from = new Date(); from.setHours(0, 0, 0, 0);
    const to = new Date(from); to.setDate(to.getDate() + 7);
    try {
      const [reservationItems, waitItems, floorItems] = await Promise.all([
        getReservations(from.toISOString(), to.toISOString()),
        getWaitlist(),
        getFloorPlan().then(items => {
          if (generation === loadGeneration.current) {
            setTablesLoaded(true);
            setTablesError("");
          }
          return items;
        }).catch(caught => {
          if (generation === loadGeneration.current) {
            setTablesLoaded(false);
            setTablesError(valueError(caught, language));
          }
          throw caught;
        })
      ]);
      if (generation !== loadGeneration.current) return;
      setReservations(reservationItems); setWaitlist(waitItems); setFloors(floorItems); setError("");
    } catch (caught) {
      if (generation !== loadGeneration.current) return;
      setError(valueError(caught, language));
    }
  };
  useEffect(() => { void load(); }, [refreshKey]);

  const availableTables = floors.flatMap((floor) => floor.tables).filter((table) => table.status === "Available" || table.status === "Reserved");
  const runReservationTransition = async (reservation: Reservation, action: "confirm" | "arrive") => {
    setReservationTransitionId(reservation.id);
    try {
      const updated = await transitionReservation(reservation, action);
      setReservations((current) => current.map((item) => item.id === updated.id ? updated : item));
      await load();
    } catch (caught) {
      setError(valueError(caught, language));
    } finally {
      setReservationTransitionId(null);
    }
  };
  const openReservation = (reservation?: Reservation) => {
    setReservationCustomer(reservation?.customerId ? {
      id: reservation.customerId, name: reservation.customerName, phone: reservation.phone,
      customerNumber: "", companyName: null, taxRegistrationNumber: null, email: null, notes: null,
      isActive: true, addresses: [], version: 0, updatedAt: ""
    } : null);
    setReservationEditor(reservation ? { ...reservation } : {
      customerName: "", phone: "", guestCount: 2, durationMinutes: 90,
      source: "Phone", specialRequests: "", tableId: null
    });
    setReservationLocalDate(toLocalDateTimeInput(reservation?.reservationAt ?? new Date(Date.now() + 3_600_000)));
    setReservationDateError("");
    setReservationError("");
  };

  const save = async () => {
    if (!reservationEditor) return;
    const resolvedDate = resolveLocalDateTime(reservationLocalDate, reservationEditor.reservationAt);
    if (resolvedDate.error) {
      setReservationDateError(localDateTimeErrorMessage(resolvedDate.error, language));
      return;
    }
    setReservationDateError("");
    if (!reservationEditor.customerName || !reservationEditor.phone) return;
    setReservationError("");
    try {
      const saved = await saveReservation({
        ...reservationEditor,
        customerName: reservationEditor.customerName,
        phone: reservationEditor.phone,
        guestCount: reservationEditor.guestCount ?? 2,
        reservationAt: resolvedDate.iso,
        durationMinutes: reservationEditor.durationMinutes ?? 90
      });
      loadGeneration.current += 1;
      setReservations((current) => current.some((item) => item.id === saved.id)
        ? current.map((item) => item.id === saved.id ? saved : item)
        : [saved, ...current]);
      setReservationEditor(null);
      onChanged();
      toast.notify({ tone: "success", title: ar ? "تم حفظ الحجز" : "Reservation saved" });
    } catch (caught) { setReservationError(valueError(caught, language)); }
  };

  return (
    <Stack>
      {error ? <Alert tone="danger" title={ar ? "تعذر التنفيذ" : "Action failed"}>{error}</Alert> : null}
      <Grid columns={2}>
        <Panel
          title={ar ? "الحجوزات القادمة" : "Upcoming Reservations"}
          actions={<Button data-testid="new-reservation" onClick={() => openReservation()}>{ar ? "حجز جديد" : "New reservation"}</Button>}
        >
          <DataTable rows={reservations} rowKey={(row) => row.id} emptyTitle={ar ? "لا توجد حجوزات" : "No reservations"} columns={[
            { key: "guest", header: ar ? "الضيف" : "Guest", cell: (row) => <button className="restaurant-link" onClick={() => openReservation(row)}><strong>{row.customerName}</strong><small>{row.phone} · {row.guestCount} {ar ? "ضيوف" : "guests"}</small></button> },
            { key: "time", header: ar ? "الوقت" : "Time", cell: (row) => new Date(row.reservationAt).toLocaleString(ar ? "ar-AE" : "en-AE") },
            { key: "table", header: ar ? "الطاولة" : "Table", cell: (row) => ar ? row.tableNameAr ?? "—" : row.tableNameEn ?? "—" },
            { key: "status", header: ar ? "الحالة" : "Status", cell: (row) => <StatusBadge tone={row.status === "Seated" ? "success" : row.status === "Arrived" ? "warning" : "info"}>{valueLabel(row.status, language)}</StatusBadge> },
            { key: "actions", header: "", cell: (row) => <Inline>
              {row.status === "Pending" ? <Button size="sm" variant="secondary" disabled={reservationTransitionId === row.id} onClick={() => void runReservationTransition(row, "confirm")}>{ar ? "تأكيد" : "Confirm"}</Button> : null}
              {row.status === "Confirmed" ? <Button size="sm" variant="secondary" disabled={reservationTransitionId === row.id} onClick={() => void runReservationTransition(row, "arrive")}>{ar ? "وصل" : "Arrived"}</Button> : null}
              {row.status === "Arrived" || row.status === "Confirmed" ? <Button size="sm" disabled={reservationTransitionId === row.id} onClick={() => { setSeatError(""); setSeatTarget(row); setSeatTableId(row.tableId ?? availableTables[0]?.id ?? ""); }}>{ar ? "إجلاس" : "Seat"}</Button> : null}
            </Inline> }
          ]} />
        </Panel>

        <Panel
          title={ar ? "قائمة الانتظار" : "Waitlist"}
          actions={<Button data-testid="add-waitlist" onClick={() => setWaitlistOpen(true)}>{ar ? "إضافة" : "Add"}</Button>}
        >
          <DataTable rows={waitlist} rowKey={(row) => row.id} emptyTitle={ar ? "لا توجد قائمة انتظار" : "Waitlist is empty"} columns={[
            { key: "guest", header: ar ? "الضيف" : "Guest", cell: (row) => <span><strong>{row.customerName}</strong><small className="restaurant-subtext">{row.guestCount} · {row.phone}</small></span> },
            { key: "wait", header: ar ? "الانتظار" : "Wait", cell: (row) => `${row.waitingMinutes}/${row.quotedWaitMinutes} ${ar ? "د" : "min"}` },
            { key: "status", header: ar ? "الحالة" : "Status", cell: (row) => <StatusBadge tone={row.status === "Notified" ? "warning" : row.status === "Seated" ? "success" : "info"}>{valueLabel(row.status, language)}</StatusBadge> },
            { key: "actions", header: "", cell: (row) => <Inline>
              {row.status === "Waiting" ? <Button size="sm" variant="secondary" onClick={async () => { await transitionWaitlistEntry(row, "notify"); await load(); }}>{ar ? "إشعار" : "Notify"}</Button> : null}
              {row.status === "Waiting" || row.status === "Notified" ? <Button size="sm" onClick={() => { setSeatError(""); setSeatTarget(row); setSeatTableId(availableTables[0]?.id ?? ""); }}>{ar ? "إجلاس" : "Seat"}</Button> : null}
            </Inline> }
          ]} />
        </Panel>
      </Grid>

      <Dialog open={Boolean(reservationEditor)} onClose={() => setReservationEditor(null)} title={ar ? "الحجز" : "Reservation"} footer={<Inline><Button variant="secondary" onClick={() => setReservationEditor(null)}>{ar ? "إلغاء" : "Cancel"}</Button><Button data-testid="save-reservation" onClick={() => void save()}>{ar ? "حفظ" : "Save"}</Button></Inline>}>
        {reservationEditor ? <Stack>
          {reservationError ? <Alert tone="danger" title={ar ? "تعذر حفظ الحجز" : "Reservation could not be saved"}><span role="alert">{reservationError}</span></Alert> : null}
          {tablesError ? <Alert tone="danger" title={ar ? "تعذر تحميل الطاولات" : "Tables could not be loaded"}>{tablesError}</Alert> : tablesLoaded && availableTables.length === 0 ? <Alert tone="info" title={ar ? "لا توجد طاولة قابلة للاختيار" : "No selectable table"}>{floors.flatMap(floor => floor.tables).length === 0 ? (ar ? "لم يُرجع مخطط الفرع أي طاولة. تحقق من إعداد الطاولات للفرع الحالي." : "This branch floor plan returned no tables. Check the current branch table setup.") : (ar ? "طاولات الفرع موجودة، لكن حالتها الحالية لا تسمح بالاختيار. راجع حالاتها." : "This branch has tables, but their current states do not allow selection. Review their states.")}</Alert> : null}
          {canReadCustomers ? <CashierCustomerSearchPicker
            language={language} label={ar ? "عميل CRM اختياري" : "Optional CRM customer"}
            value={reservationEditor.customerId ?? ""} selectedCustomer={reservationCustomer}
            initialCustomers={noReservationCustomers}
            onSelect={customer => {
              setReservationCustomer(customer);
              setReservationEditor({ ...reservationEditor, customerId: customer?.id ?? null,
                customerName: customer?.name ?? reservationEditor.customerName,
                phone: customer?.phone ?? reservationEditor.phone });
            }}
            emptyLabel={ar ? "ضيف غير مسجل" : "Unregistered guest"}
            searchTestId="reservation-customer-search" selectTestId="reservation-customer-select" /> : null}
          <Grid columns={2}>
            <Field label={ar ? "اسم العميل" : "Customer name"} value={reservationEditor.customerName ?? ""} onChange={(event) => setReservationEditor({ ...reservationEditor, customerName: event.target.value })} />
            <Field label={ar ? "الهاتف" : "Phone"} value={reservationEditor.phone ?? ""} onChange={(event) => setReservationEditor({ ...reservationEditor, phone: event.target.value })} />
            <Field label={ar ? "عدد الضيوف" : "Guests"} type="number" min={1} value={reservationEditor.guestCount ?? 2} onChange={(event) => setReservationEditor({ ...reservationEditor, guestCount: Number(event.target.value) })} />
            <Field label={ar ? "التاريخ والوقت" : "Date & time"} hint={localTimeZoneHint(language)} error={reservationDateError || undefined} type="datetime-local" value={reservationLocalDate} onChange={(event) => { setReservationLocalDate(event.target.value); setReservationDateError(""); }} />
            <SelectField label={ar ? "الطاولة" : "Table"} value={reservationEditor.tableId ?? ""} onChange={(event) => setReservationEditor({ ...reservationEditor, tableId: event.target.value || null })}>
              <option value="">{ar ? "تحديد لاحقًا" : "Assign later"}</option>
              {availableTables.map((table) => <option key={table.id} value={table.id}>{ar ? table.nameAr : table.nameEn} · {table.capacity}</option>)}
            </SelectField>
            <Field label={ar ? "المدة بالدقائق" : "Duration minutes"} type="number" min={15} value={reservationEditor.durationMinutes ?? 90} onChange={(event) => setReservationEditor({ ...reservationEditor, durationMinutes: Number(event.target.value) })} />
          </Grid>
          <TextAreaField label={ar ? "طلبات خاصة" : "Special requests"} value={reservationEditor.specialRequests ?? ""} onChange={(event) => setReservationEditor({ ...reservationEditor, specialRequests: event.target.value })} />
        </Stack> : null}
      </Dialog>

      <Dialog open={waitlistOpen} onClose={() => setWaitlistOpen(false)} title={ar ? "إضافة لقائمة الانتظار" : "Add to waitlist"} footer={<Inline><Button variant="secondary" onClick={() => setWaitlistOpen(false)}>{ar ? "إلغاء" : "Cancel"}</Button><Button onClick={async () => { try { await addWaitlistEntry({ customerName: waitName, phone: waitPhone, guestCount: Number(waitGuests), quotedWaitMinutes: Number(waitMinutes), notes: null }); setWaitlistOpen(false); await load(); onChanged(); } catch (caught) { setError(valueError(caught, language)); } }}>{ar ? "إضافة" : "Add"}</Button></Inline>}>
        <Grid columns={2}>
          <Field label={ar ? "الاسم" : "Name"} value={waitName} onChange={(event) => setWaitName(event.target.value)} />
          <Field label={ar ? "الهاتف" : "Phone"} value={waitPhone} onChange={(event) => setWaitPhone(event.target.value)} />
          <Field label={ar ? "الضيوف" : "Guests"} type="number" value={waitGuests} onChange={(event) => setWaitGuests(event.target.value)} />
          <Field label={ar ? "الانتظار المتوقع" : "Quoted wait"} type="number" value={waitMinutes} onChange={(event) => setWaitMinutes(event.target.value)} />
        </Grid>
      </Dialog>

      <Dialog open={Boolean(seatTarget)} onClose={() => setSeatTarget(null)} title={ar ? "اختيار الطاولة" : "Select table"} footer={<Inline><Button variant="secondary" onClick={() => setSeatTarget(null)}>{ar ? "إلغاء" : "Cancel"}</Button><Button data-testid="seat-and-open-order" disabled={!seatTableId} onClick={async () => { if (!seatTarget) return; setSeatError(""); try { if ("reservationNumber" in seatTarget) await transitionReservation(seatTarget, "seat", seatTableId); else await transitionWaitlistEntry(seatTarget, "seat", seatTableId); setSeatTarget(null); await load(); onChanged(); } catch (caught) { setSeatError(valueError(caught, language)); } }}>{ar ? "إجلاس وفتح طلب" : "Seat & open order"}</Button></Inline>}>
        {seatError ? <Alert tone="danger" title={ar ? "تعذر الإجلاس" : "Seating failed"}><span>{seatError}</span></Alert> : null}
        <SelectField label={ar ? "الطاولة" : "Table"} value={seatTableId} onChange={(event) => { setSeatTableId(event.target.value); setSeatError(""); }}>
          {availableTables.map((table) => <option key={table.id} value={table.id}>{ar ? table.nameAr : table.nameEn} · {table.capacity}</option>)}
        </SelectField>
      </Dialog>
    </Stack>
  );
}

function DeliveryBoard({
  language,
  canDispatch,
  refreshKey,
  onChanged
}: {
  language: Language;
  canDispatch: boolean;
  refreshKey: number;
  onChanged: () => void;
}) {
  const ar = language === "ar";
  const [orders, setOrders] = useState<DeliveryOrder[]>([]);
  const [drivers, setDrivers] = useState<DeliveryDriver[]>([]);
  const [assignTarget, setAssignTarget] = useState<DeliveryOrder | null>(null);
  const [driverId, setDriverId] = useState("");
  const [driverEditor, setDriverEditor] = useState<Partial<DeliveryDriver> | null>(null);
  const [error, setError] = useState("");

  const load = async () => {
    try {
      const [deliveryOrders, driverItems] = await Promise.all([getDeliveryBoard(), getDeliveryDrivers()]);
      setOrders(deliveryOrders); setDrivers(driverItems); setDriverId((value) => value || driverItems.find((item) => item.status === "Available")?.id || ""); setError("");
    } catch (caught) { setError(message(caught)); }
  };
  useEffect(() => { void load(); }, [refreshKey]);

  const transition = async (order: DeliveryOrder, action: "pickup" | "out-for-delivery" | "deliver" | "fail") => {
    try { await transitionDeliveryOrder(order, action); await load(); onChanged(); } catch (caught) { setError(message(caught)); }
  };

  return (
    <Stack>
      {error ? <Alert tone="danger" title={ar ? "تعذر التنفيذ" : "Action failed"}>{error}</Alert> : null}
      <Grid columns={3}>
        <KpiCard label={ar ? "طلبات توصيل" : "Delivery orders"} value={orders.length} />
        <KpiCard label={ar ? "دون سائق" : "Unassigned"} value={orders.filter((order) => !order.assignmentId).length} />
        <KpiCard label={ar ? "سائقون متاحون" : "Available drivers"} value={drivers.filter((driver) => driver.status === "Available").length} />
      </Grid>
      <Panel title={ar ? "لوحة التوصيل" : "Delivery Dispatch Board"}>
        <DataTable rows={orders} rowKey={(row) => row.orderId} emptyTitle={ar ? "لا توجد طلبات توصيل فعالة" : "No active deliveries"} columns={[
          { key: "order", header: ar ? "الطلب" : "Order", cell: (row) => <span><strong>{row.orderNumber}</strong><small className="restaurant-subtext">{row.customerName ?? "—"} · {row.customerPhone ?? "—"}</small></span> },
          { key: "address", header: ar ? "العنوان" : "Address", cell: (row) => row.address ?? "—" },
          { key: "time", header: ar ? "الوقت" : "Time", cell: (row) => <StatusBadge tone={row.isLate ? "danger" : "neutral"}>{row.ageMinutes} {ar ? "د" : "min"}</StatusBadge> },
          { key: "driver", header: ar ? "السائق" : "Driver", cell: (row) => row.driverName ?? "—" },
          { key: "status", header: ar ? "الحالة" : "Status", cell: (row) => <StatusBadge tone={row.assignmentStatus === "Delivered" ? "success" : row.isLate ? "danger" : "info"}>{row.assignmentStatus ?? row.deliveryStatus}</StatusBadge> },
          { key: "actions", header: "", cell: (row) => canDispatch ? <Inline>
            {!row.assignmentId ? <Button size="sm" onClick={() => { setAssignTarget(row); setDriverId(drivers.find((driver) => driver.status === "Available")?.id ?? ""); }}>{ar ? "تعيين" : "Assign"}</Button> : null}
            {row.assignmentStatus === "Assigned" && row.fulfillmentStatus === "Ready" ? <Button size="sm" variant="secondary" onClick={() => void transition(row, "pickup")}>{ar ? "استلام" : "Pickup"}</Button> : null}
            {row.assignmentStatus === "PickedUp" ? <Button size="sm" onClick={() => void transition(row, "out-for-delivery")}>{ar ? "خرج للتوصيل" : "Out"}</Button> : null}
            {row.assignmentStatus === "OutForDelivery" ? <Button size="sm" onClick={() => void transition(row, "deliver")}>{ar ? "تم التسليم" : "Delivered"}</Button> : null}
          </Inline> : null }
        ]} />
      </Panel>
      <Panel title={ar ? "السائقون" : "Drivers"} actions={canDispatch ? <Button onClick={() => setDriverEditor({ name: "", phone: "", status: "Available", isActive: true, vehicleNumber: "" })}>{ar ? "سائق جديد" : "New driver"}</Button> : null}>
        <DataTable rows={drivers} rowKey={(row) => row.id} emptyTitle={ar ? "لا يوجد سائقون" : "No drivers"} columns={[
          { key: "driver", header: ar ? "السائق" : "Driver", cell: (row) => <button className="restaurant-link" onClick={() => setDriverEditor(row)}><strong>{row.name}</strong><small>{row.driverNumber} · {row.vehicleNumber ?? "—"}</small></button> },
          { key: "status", header: ar ? "الحالة" : "Status", cell: (row) => <StatusBadge tone={row.status === "Available" ? "success" : row.status === "OnDelivery" ? "warning" : "neutral"}>{valueLabel(row.status, language)}</StatusBadge> },
          { key: "active", header: ar ? "نشطة" : "Active", cell: (row) => row.activeAssignmentCount },
          { key: "completed", header: ar ? "اليوم" : "Today", cell: (row) => row.completedTodayCount },
          { key: "onTime", header: ar ? "في الوقت" : "On time", cell: (row) => `${row.onTimePercent}%` }
        ]} />
      </Panel>
      <Dialog open={Boolean(assignTarget)} onClose={() => setAssignTarget(null)} title={ar ? "تعيين سائق" : "Assign driver"} footer={<Inline><Button variant="secondary" onClick={() => setAssignTarget(null)}>{ar ? "إلغاء" : "Cancel"}</Button><Button data-testid="confirm-delivery-assignment" disabled={!driverId} onClick={async () => { if (!assignTarget) return; try { await assignDeliveryOrder(assignTarget, driverId, 25, null, null); setAssignTarget(null); await load(); onChanged(); } catch (caught) { setError(message(caught)); } }}>{ar ? "تعيين" : "Assign"}</Button></Inline>}>
        <SelectField label={ar ? "السائق" : "Driver"} value={driverId} onChange={(event) => setDriverId(event.target.value)}>
          {drivers.filter((driver) => driver.status === "Available" || driver.id === assignTarget?.driverId).map((driver) => <option key={driver.id} value={driver.id}>{driver.name} · {driver.vehicleNumber ?? driver.phone}</option>)}
        </SelectField>
      </Dialog>
      <Dialog open={Boolean(driverEditor)} onClose={() => setDriverEditor(null)} title={ar ? "بيانات السائق" : "Driver"} footer={<Inline><Button variant="secondary" onClick={() => setDriverEditor(null)}>{ar ? "إلغاء" : "Cancel"}</Button><Button onClick={async () => { if (!driverEditor?.name || !driverEditor.phone) return; try { await saveDeliveryDriver({ ...driverEditor, name: driverEditor.name, phone: driverEditor.phone, status: (driverEditor.status ?? "Available") as DriverStatus }); setDriverEditor(null); await load(); onChanged(); } catch (caught) { setError(message(caught)); } }}>{ar ? "حفظ" : "Save"}</Button></Inline>}>
        {driverEditor ? <Grid columns={2}>
          <Field label={ar ? "الاسم" : "Name"} value={driverEditor.name ?? ""} onChange={(event) => setDriverEditor({ ...driverEditor, name: event.target.value })} />
          <Field label={ar ? "الهاتف" : "Phone"} value={driverEditor.phone ?? ""} onChange={(event) => setDriverEditor({ ...driverEditor, phone: event.target.value })} />
          <Field label={ar ? "المركبة" : "Vehicle"} value={driverEditor.vehicleNumber ?? ""} onChange={(event) => setDriverEditor({ ...driverEditor, vehicleNumber: event.target.value })} />
          <SelectField label={ar ? "الحالة" : "Status"} value={driverEditor.status ?? "Available"} onChange={(event) => setDriverEditor({ ...driverEditor, status: event.target.value as DriverStatus })}>
            {(["Offline", "Available", "Assigned", "OnDelivery", "Break"] as DriverStatus[]).map((status) => <option key={status}>{status}</option>)}
          </SelectField>
        </Grid> : null}
      </Dialog>
    </Stack>
  );
}

function RecipesBoard({
  language,
  refreshKey,
  onChanged
}: {
  language: Language;
  refreshKey: number;
  onChanged: () => void;
}) {
  const ar = language === "ar";
  const [recipes, setRecipes] = useState<Recipe[]>([]);
  const [products, setProducts] = useState<Product[]>([]);
  const [editor, setEditor] = useState<Partial<Recipe> | null>(null);
  const [error, setError] = useState("");
  const variants = useMemo(() => flattenVariants(products), [products]);
  const sellable = variants.filter((variant) => variant.sellable);
  const ingredients = variants.filter((variant) => variant.tracked && !variant.sellable);

  const load = async () => {
    try { const [recipeItems, productItems] = await Promise.all([getRecipes(), getProducts()]); setRecipes(recipeItems); setProducts(productItems); setError(""); } catch (caught) { setError(message(caught)); }
  };
  useEffect(() => { void load(); }, [refreshKey]);

  const save = async () => {
    if (!editor?.productVariantId || !editor.code || !editor.nameAr || !editor.nameEn || !editor.components?.length) return;
    try {
      await saveRecipe({
        ...editor,
        productVariantId: editor.productVariantId,
        code: editor.code,
        nameAr: editor.nameAr,
        nameEn: editor.nameEn,
        status: editor.status ?? "Active",
        yieldQuantityMilli: editor.yieldQuantityMilli ?? 1000,
        preparationMinutes: editor.preparationMinutes ?? 10,
        components: editor.components
      });
      setEditor(null); await load(); onChanged();
    } catch (caught) { setError(message(caught)); }
  };

  return (
    <Stack>
      {error ? <Alert tone="danger" title={ar ? "تعذر التنفيذ" : "Action failed"}>{error}</Alert> : null}
      <Panel title={ar ? "الوصفات وتكلفة المكونات" : "Recipes & Ingredient Cost"} actions={<Button onClick={() => setEditor({ productVariantId: sellable[0]?.id ?? "", code: "REC-NEW", nameAr: "وصفة جديدة", nameEn: "New Recipe", status: "Draft", yieldQuantityMilli: 1000, preparationMinutes: 10, instructions: "", components: ingredients[0] ? [{ ingredientVariantId: ingredients[0].id, quantityMilli: 100, wasteBasisPoints: 0, isOptional: false, sortOrder: 10 }] : [] })}>{ar ? "وصفة جديدة" : "New recipe"}</Button>}>
        <DataTable rows={recipes} rowKey={(row) => row.id} emptyTitle={ar ? "لا توجد وصفات" : "No recipes"} columns={[
          { key: "recipe", header: ar ? "الوصفة" : "Recipe", cell: (row) => <button className="restaurant-link" onClick={() => setEditor(structuredClone(row))}><strong>{ar ? row.nameAr : row.nameEn}</strong><small>{row.code} · {row.sku}</small></button> },
          { key: "item", header: ar ? "الصنف المباع" : "Sellable item", cell: (row) => ar ? row.productNameAr : row.productNameEn },
          { key: "components", header: ar ? "المكونات" : "Components", cell: (row) => row.components.length },
          { key: "time", header: ar ? "وقت التحضير" : "Prep time", cell: (row) => `${row.preparationMinutes} ${ar ? "د" : "min"}` },
          { key: "cost", header: ar ? "التكلفة" : "Cost", cell: (row) => money(row.estimatedCostMinor) },
          { key: "status", header: ar ? "الحالة" : "Status", cell: (row) => <StatusBadge tone={row.status === "Active" ? "success" : "neutral"}>{valueLabel(row.status, language)}</StatusBadge> }
        ]} />
      </Panel>
      <Dialog open={Boolean(editor)} onClose={() => setEditor(null)} title={ar ? "محرر الوصفة" : "Recipe editor"} footer={<Inline><Button variant="secondary" onClick={() => setEditor(null)}>{ar ? "إلغاء" : "Cancel"}</Button><Button onClick={() => void save()}>{ar ? "حفظ" : "Save"}</Button></Inline>}>
        {editor ? <Stack>
          <Grid columns={2}>
            <SelectField label={ar ? "الصنف المباع" : "Sellable item"} value={editor.productVariantId ?? ""} onChange={(event) => setEditor({ ...editor, productVariantId: event.target.value })}>
              {sellable.map((variant) => <option key={variant.id} value={variant.id}>{ar ? variant.productNameAr : variant.productNameEn} · {variant.sku}</option>)}
            </SelectField>
            <Field label={ar ? "الرمز" : "Code"} value={editor.code ?? ""} onChange={(event) => setEditor({ ...editor, code: event.target.value.toUpperCase() })} />
            <Field label={ar ? "الاسم العربي" : "Arabic name"} value={editor.nameAr ?? ""} onChange={(event) => setEditor({ ...editor, nameAr: event.target.value })} />
            <Field label={ar ? "الاسم الإنجليزي" : "English name"} value={editor.nameEn ?? ""} onChange={(event) => setEditor({ ...editor, nameEn: event.target.value })} />
            <Field label={ar ? "الناتج بالملي" : "Yield milli"} type="number" min={1} value={editor.yieldQuantityMilli ?? 1000} onChange={(event) => setEditor({ ...editor, yieldQuantityMilli: Number(event.target.value) })} />
            <Field label={ar ? "وقت التحضير" : "Preparation minutes"} type="number" min={0} value={editor.preparationMinutes ?? 10} onChange={(event) => setEditor({ ...editor, preparationMinutes: Number(event.target.value) })} />
          </Grid>
          <TextAreaField label={ar ? "تعليمات التحضير" : "Instructions"} value={editor.instructions ?? ""} onChange={(event) => setEditor({ ...editor, instructions: event.target.value })} />
          <Card title={ar ? "المكونات" : "Components"} actions={<Button size="sm" variant="secondary" onClick={() => setEditor({ ...editor, components: [...(editor.components ?? []), { ingredientVariantId: ingredients[0]?.id ?? "", quantityMilli: 100, wasteBasisPoints: 0, isOptional: false, sortOrder: (editor.components?.length ?? 0) * 10 + 10 }] })}>{ar ? "إضافة مكون" : "Add component"}</Button>}>
            <Stack>
              {(editor.components ?? []).map((component, index) => <Grid columns={3} key={component.id ?? index}>
                <SelectField label={ar ? "المكون" : "Ingredient"} value={component.ingredientVariantId} onChange={(event) => setEditor({ ...editor, components: editor.components!.map((item, itemIndex) => itemIndex === index ? { ...item, ingredientVariantId: event.target.value } : item) })}>
                  {ingredients.map((variant) => <option key={variant.id} value={variant.id}>{ar ? variant.productNameAr : variant.productNameEn} · {variant.sku}</option>)}
                </SelectField>
                <Field label={ar ? "الكمية بالملي" : "Quantity milli"} type="number" min={1} value={component.quantityMilli} onChange={(event) => setEditor({ ...editor, components: editor.components!.map((item, itemIndex) => itemIndex === index ? { ...item, quantityMilli: Number(event.target.value) } : item) })} />
                <Field label={ar ? "هدر basis points" : "Waste bps"} type="number" min={0} max={10000} value={component.wasteBasisPoints} onChange={(event) => setEditor({ ...editor, components: editor.components!.map((item, itemIndex) => itemIndex === index ? { ...item, wasteBasisPoints: Number(event.target.value) } : item) })} />
              </Grid>)}
            </Stack>
          </Card>
        </Stack> : null}
      </Dialog>
    </Stack>
  );
}

function ConfigurationBoard({
  language,
  canManage,
  refreshKey,
  onChanged
}: {
  language: Language;
  canManage: boolean;
  refreshKey: number;
  onChanged: () => void;
}) {
  const ar = language === "ar";
  const [stations, setStations] = useState<KitchenStation[]>([]);
  const [rules, setRules] = useState<KitchenRoutingRule[]>([]);
  const [products, setProducts] = useState<Product[]>([]);
  const [stationEditor, setStationEditor] = useState<Partial<KitchenStation> | null>(null);
  const [ruleEditor, setRuleEditor] = useState<Partial<KitchenRoutingRule> | null>(null);
  const [error, setError] = useState("");
  const variants = useMemo(() => flattenVariants(products), [products]);

  const load = async () => {
    try { const [stationItems, ruleItems, productItems] = await Promise.all([getKitchenStations(), getKitchenRoutingRules(), getProducts()]); setStations(stationItems); setRules(ruleItems); setProducts(productItems); setError(""); } catch (caught) { setError(message(caught)); }
  };
  useEffect(() => { void load(); }, [refreshKey]);

  return (
    <Stack>
      {error ? <Alert tone="danger" title={ar ? "تعذر التنفيذ" : "Action failed"}>{error}</Alert> : null}
      <Panel title={ar ? "محطات المطبخ" : "Kitchen Stations"} actions={canManage ? <Button onClick={() => setStationEditor({ code: "NEW", nameAr: "محطة جديدة", nameEn: "New Station", stationType: "General", warningAfterMinutes: 10, criticalAfterMinutes: 20, isActive: true, isDefault: false, isExpo: false, sortOrder: 50 })}>{ar ? "محطة جديدة" : "New station"}</Button> : null}>
        <Grid columns={4}>
          {stations.map((station) => <Card key={station.id} title={ar ? station.nameAr : station.nameEn} description={`${station.code} · ${station.stationType}`} actions={canManage ? <Button size="sm" variant="secondary" onClick={() => setStationEditor(station)}>{ar ? "تعديل" : "Edit"}</Button> : null}>
            <Stack><Inline>{station.isDefault ? <StatusBadge tone="info">Default</StatusBadge> : null}{station.isExpo ? <StatusBadge tone="success">Expo</StatusBadge> : null}</Inline><strong>{station.activeTicketCount} {ar ? "تذكرة" : "tickets"}</strong><small className="restaurant-subtext">{station.averagePreparationMinutes} min avg · {station.warningAfterMinutes}/{station.criticalAfterMinutes}</small></Stack>
          </Card>)}
        </Grid>
      </Panel>
      <Panel title={ar ? "قواعد توجيه الأصناف" : "Item Routing Rules"} actions={canManage ? <Button onClick={() => setRuleEditor({ stationId: stations[0]?.id ?? "", productVariantId: variants[0]?.id ?? null, productId: null, categoryId: null, priority: 100, isActive: true })}>{ar ? "قاعدة جديدة" : "New rule"}</Button> : null}>
        <DataTable rows={rules} rowKey={(row) => row.id} emptyTitle={ar ? "لا توجد قواعد" : "No routing rules"} columns={[
          { key: "target", header: ar ? "الهدف" : "Target", cell: (row) => <button className="restaurant-link" onClick={() => setRuleEditor(row)}><strong>{row.productVariantName ?? row.productName ?? row.categoryName}</strong><small>{row.productVariantId ? "Variant" : row.productId ? "Product" : "Category"}</small></button> },
          { key: "station", header: ar ? "المحطة" : "Station", cell: (row) => row.stationName },
          { key: "priority", header: ar ? "الأولوية" : "Priority", cell: (row) => row.priority },
          { key: "status", header: ar ? "الحالة" : "Status", cell: (row) => <StatusBadge tone={row.isActive ? "success" : "neutral"}>{row.isActive ? "Active" : "Inactive"}</StatusBadge> }
        ]} />
      </Panel>
      <Dialog open={Boolean(stationEditor)} onClose={() => setStationEditor(null)} title={ar ? "محطة المطبخ" : "Kitchen station"} footer={<Inline><Button variant="secondary" onClick={() => setStationEditor(null)}>{ar ? "إلغاء" : "Cancel"}</Button><Button disabled={!canManage} onClick={async () => { if (!stationEditor?.code || !stationEditor.nameAr || !stationEditor.nameEn || !stationEditor.stationType) return; try { await saveKitchenStation({ ...stationEditor, code: stationEditor.code, nameAr: stationEditor.nameAr, nameEn: stationEditor.nameEn, stationType: stationEditor.stationType }); setStationEditor(null); await load(); onChanged(); } catch (caught) { setError(message(caught)); } }}>{ar ? "حفظ" : "Save"}</Button></Inline>}>
        {stationEditor ? <Stack><Grid columns={2}>
          <Field label={ar ? "الرمز" : "Code"} value={stationEditor.code ?? ""} onChange={(event) => setStationEditor({ ...stationEditor, code: event.target.value.toUpperCase() })} />
          <SelectField label={ar ? "النوع" : "Type"} value={stationEditor.stationType ?? "General"} onChange={(event) => setStationEditor({ ...stationEditor, stationType: event.target.value as KitchenStation["stationType"] })}>{["General", "HotKitchen", "ColdKitchen", "Bar", "Pastry", "Expo"].map((type) => <option key={type}>{type}</option>)}</SelectField>
          <Field label={ar ? "الاسم العربي" : "Arabic name"} value={stationEditor.nameAr ?? ""} onChange={(event) => setStationEditor({ ...stationEditor, nameAr: event.target.value })} />
          <Field label={ar ? "الاسم الإنجليزي" : "English name"} value={stationEditor.nameEn ?? ""} onChange={(event) => setStationEditor({ ...stationEditor, nameEn: event.target.value })} />
          <Field label={ar ? "تحذير بعد" : "Warning after"} type="number" min={1} value={stationEditor.warningAfterMinutes ?? 10} onChange={(event) => setStationEditor({ ...stationEditor, warningAfterMinutes: Number(event.target.value) })} />
          <Field label={ar ? "حرج بعد" : "Critical after"} type="number" min={2} value={stationEditor.criticalAfterMinutes ?? 20} onChange={(event) => setStationEditor({ ...stationEditor, criticalAfterMinutes: Number(event.target.value) })} />
        </Grid><Inline><Switch label="Default" checked={stationEditor.isDefault ?? false} onChange={(event) => setStationEditor({ ...stationEditor, isDefault: event.target.checked })} /><Switch label="Expo" checked={stationEditor.isExpo ?? false} onChange={(event) => setStationEditor({ ...stationEditor, isExpo: event.target.checked })} /><Switch label="Active" checked={stationEditor.isActive ?? true} onChange={(event) => setStationEditor({ ...stationEditor, isActive: event.target.checked })} /></Inline></Stack> : null}
      </Dialog>
      <Dialog open={Boolean(ruleEditor)} onClose={() => setRuleEditor(null)} title={ar ? "قاعدة التوجيه" : "Routing rule"} footer={<Inline><Button variant="secondary" onClick={() => setRuleEditor(null)}>{ar ? "إلغاء" : "Cancel"}</Button><Button disabled={!canManage} onClick={async () => { if (!ruleEditor?.stationId) return; try { await saveKitchenRoutingRule({ ...ruleEditor, stationId: ruleEditor.stationId, priority: ruleEditor.priority ?? 100 }); setRuleEditor(null); await load(); onChanged(); } catch (caught) { setError(message(caught)); } }}>{ar ? "حفظ" : "Save"}</Button></Inline>}>
        {ruleEditor ? <Stack>
          <SelectField label={ar ? "المحطة" : "Station"} value={ruleEditor.stationId ?? ""} onChange={(event) => setRuleEditor({ ...ruleEditor, stationId: event.target.value })}>{stations.map((station) => <option key={station.id} value={station.id}>{ar ? station.nameAr : station.nameEn}</option>)}</SelectField>
          <SelectField label={ar ? "متغير المنتج" : "Product variant"} value={ruleEditor.productVariantId ?? ""} onChange={(event) => setRuleEditor({ ...ruleEditor, productVariantId: event.target.value || null, productId: null, categoryId: null })}><option value="">—</option>{variants.filter((variant) => variant.sellable).map((variant) => <option key={variant.id} value={variant.id}>{ar ? variant.productNameAr : variant.productNameEn} · {variant.sku}</option>)}</SelectField>
          <Field label={ar ? "الأولوية" : "Priority"} type="number" value={ruleEditor.priority ?? 100} onChange={(event) => setRuleEditor({ ...ruleEditor, priority: Number(event.target.value) })} />
          <Switch label={ar ? "نشطة" : "Active"} checked={ruleEditor.isActive ?? true} onChange={(event) => setRuleEditor({ ...ruleEditor, isActive: event.target.checked })} />
        </Stack> : null}
      </Dialog>
    </Stack>
  );
}

function flattenVariants(products: Product[]): VariantOption[] {
  return products.flatMap((product) => product.variants.filter((variant) => Boolean(variant.id)).map((variant) => ({
    id: variant.id!,
    productId: product.id!,
    categoryId: product.categoryId,
    productNameAr: product.nameAr,
    productNameEn: product.nameEn,
    variantNameAr: variant.nameAr,
    variantNameEn: variant.nameEn,
    sku: variant.sku,
    tracked: product.trackInventory,
    sellable: product.sellable
  })));
}

function message(error: unknown): string {
  return error instanceof Error ? error.message : "Unexpected operation failure.";
}

function money(minor: number): string {
  return formatMoneyMinor(minor, "AED", "en-AE");
}
